import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import type { CoverageEntitiesResult, ModelCServiceStop, RouteDirection, RouteMetricsResult } from '../api';
import {
  areasToGeoJSON,
  type AreaLayerKey,
  LAYERS,
  type LayerKey,
  type LayerTab,
  pointFeaturesFor,
  pointsToGeoJSON,
  type PointLayerKey,
  SECONDARY_TYPE_LABELS,
  type SelectedFeature,
} from './layers';
import type { AreaAccessibilityTier } from '../api';

const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY as string | undefined) ?? '';

/**
 * `map.once('load', fn)` como fallback de "todavía no está listo" solo
 * tiene sentido en el montaje inicial — `'load'` dispara una única vez en
 * toda la vida del mapa. En un mapa persistente entre pestañas, estos
 * efectos reactivos pueden re-ejecutarse mucho después de ese primer
 * `'load'`, con `isStyleLoaded()` transitoriamente en `false` (mientras
 * OTRA fuente de la misma tanda de efectos está siendo actualizada) — con
 * `.once('load', ...)` esa actualización quedaba perdida para siempre, sin
 * error visible (bug real, encontrado en QA: activar una capa en el panel
 * derecho no mostraba nada en el mapa). `'idle'` sí dispara cada vez que el
 * mapa termina de procesar cambios pendientes, así que sirve como
 * fallback repetible.
 */
function runWhenReady(map: maplibregl.Map, fn: () => void): void {
  if (map.isStyleLoaded()) fn();
  else map.once('idle', fn);
}

export type TerritorialMapMode = 'coverage' | 'overview' | 'speed' | 'infrastructure' | 'connectivity';

export interface SpeedGradientStops {
  IDA: Array<[number, string]>;
  VUELTA: Array<[number, string]>;
}

export interface TerritorialMapProps {
  routeCoordinates: [number, number][];
  direction: RouteDirection;
  isCircuit: boolean;
  coveragePolygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  showCoverage: boolean;
  routeMetrics: RouteMetricsResult | null;
  coverageEntities: CoverageEntitiesResult | null;
  activeLayers: Set<LayerKey>;
  onSelectFeature: (f: SelectedFeature) => void;
  className?: string;
  /**
   * `coverage` (default, comportamiento histórico): una sola línea
   * (`routeCoordinates`/`direction`) + capas POI + isócrona — usado por
   * Ficha (siempre) y por la pestaña Cobertura de LineOperationsPage.
   * `overview`/`speed`/`infrastructure`: ambas direcciones a la vez
   * (`ida`/`vuelta`) — Recorrido sin gradiente, Velocidades con
   * `speedStops`, Infraestructura vial con `pavementStops` + capas POI
   * propias (semáforos/reductores/cruces, ver `layerScope`). Nunca cambia
   * qué instancia de mapa existe, solo qué fuentes/capas están visibles —
   * LineOperationsPage mantiene un único `<TerritorialMap>` montado entre
   * pestañas.
   */
  mode?: TerritorialMapMode;
  ida?: [number, number][];
  vuelta?: [number, number][];
  showPois?: boolean;
  /** Cobertura e Infraestructura vial comparten el mismo `activeLayers` Set — sin este filtro, una capa marcada en una pestaña seguía visible (aunque "fuera de lugar") al entrar a la otra. */
  layerScope?: LayerTab;
  speedStops?: SpeedGradientStops | null;
  pavementStops?: SpeedGradientStops | null;
  syntaxStops?: SpeedGradientStops | null;
  serviceStops?: ModelCServiceStop[];
  selectedServiceStopId?: string | null;
  onSelectServiceStop?: (stop: ModelCServiceStop) => void;
}

function serviceStopsToGeoJSON(stops: ModelCServiceStop[], selectedId: string | null | undefined): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: stops.map(stop => ({
      type: 'Feature',
      properties: {
        facilityId: stop.facilityId,
        sequence: stop.sequence,
        label: String(stop.sequence),
        selected: stop.facilityId === selectedId,
      },
      geometry: stop.displayGeometry,
    })),
  };
}

/**
 * Mapa interactivo con capas de entidades — núcleo compartido entre Ficha
 * y LineOperationsPage. Todas las fuentes/capas se crean-o-actualizan vía
 * efectos reactivos (no solo dentro del cierre de montaje) para que un
 * consumidor pueda mantener la MISMA instancia de mapa viva a través de
 * cambios de pestaña/dirección sin volver a montar — Ficha, que sigue
 * remontando por `key={direction}`, se comporta exactamente igual que
 * antes porque esos mismos efectos simplemente corren una vez al montar.
 */
export function TerritorialMap(props: TerritorialMapProps): JSX.Element {
  const mapRef = useRef<maplibregl.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const serviceStopsRef = useRef<ModelCServiceStop[]>([]);
  const onSelectServiceStopRef = useRef<TerritorialMapProps['onSelectServiceStop']>(undefined);
  const mode = props.mode ?? 'coverage';
  const showPois = props.showPois ?? true;
  const layerScope = props.layerScope;
  serviceStopsRef.current = props.serviceStops ?? [];
  onSelectServiceStopRef.current = props.onSelectServiceStop;
  const isLayerVisible = (layer: (typeof LAYERS)[number]): boolean =>
    showPois && props.activeLayers.has(layer.key) && (!layerScope || layer.tab === layerScope);

  // ---- Montaje: solo el mapa base + fitBounds inicial. Todo lo demás
  // (líneas, POI, cobertura) lo crean los efectos de abajo, para que
  // también corran en cada actualización sin re-montar. ----
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          basemap: {
            type: 'raster',
            tiles: [`https://api.maptiler.com/maps/positron/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`],
            tileSize: 256,
            attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
          },
        },
        layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
        // Requerido por cualquier layer 'symbol' con text-field (p.ej. las
        // etiquetas numéricas de paradas Model C) — sin esto MapLibre
        // rechaza el addLayer con "requires a style glyphs property".
        // Mismo proveedor/key que ya usa el basemap, ningún dependencia
        // externa nueva.
        glyphs: `https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=${MAPTILER_KEY}`,
      },
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    // Ver nota histórica: el contenedor puede no tener su tamaño final
    // resuelto en el primer paint (panel flotante todavía reflowing).
    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(containerRef.current);

    map.on('load', () => {
      map.resize();
      const fitCoords =
        mode === 'coverage' ? props.routeCoordinates : (props.ida?.length ?? 0) > 1 ? props.ida! : props.vuelta ?? [];
      if (fitCoords.length > 1) {
        const bounds = fitCoords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(fitCoords[0], fitCoords[0]));
        map.fitBounds(bounds, { padding: 32, duration: 0 });
      }
    });

    return (): void => {
      resizeObserver.disconnect();
      mapRef.current = null;
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fitBounds inicial usa el valor de montaje a propósito; actualizaciones posteriores no vuelven a encuadrar
  }, []);

  // ---- Línea única (modo `coverage`) — misma fuente/capa que siempre,
  // ahora crear-o-actualizar en vez de solo crear, para poder vivir dentro
  // de un mapa persistente que cambia de dirección sin remontar. ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== 'coverage') return;
    const apply = (): void => {
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: props.routeCoordinates },
      };
      const color = props.isCircuit ? '#6b3fa0' : props.direction === 'VUELTA' ? '#1c6fd6' : '#e5622e';
      const source = map.getSource('route-line') as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(feature);
        if (map.getLayer('l-route-line')) map.setPaintProperty('l-route-line', 'line-color', color);
      } else if (props.routeCoordinates.length > 1) {
        map.addSource('route-line', { type: 'geojson', data: feature });
        map.addLayer({
          id: 'l-route-line',
          type: 'line',
          source: 'route-line',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': color, 'line-width': 4 },
        });
      }
    };
    runWhenReady(map, apply);
  }, [mode, props.routeCoordinates, props.direction, props.isCircuit]);

  // ---- Doble línea (modos `overview`/`speed`) — IDA + VUELTA (o solo IDA
  // para CIRCUIT) siempre visibles a la vez, sin POI — pestañas
  // Recorrido/Velocidades. `lineMetrics:true` habilita `line-gradient`
  // para el modo `speed`. ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode === 'coverage') return;
    const apply = (): void => {
      const dirs: Array<{ key: 'IDA' | 'VUELTA'; coords: [number, number][] | undefined; color: string }> = [
        { key: 'IDA', coords: props.ida, color: props.isCircuit ? '#6b3fa0' : '#4fb0ff' },
        { key: 'VUELTA', coords: props.isCircuit ? undefined : props.vuelta, color: '#ff8a4c' },
      ];
      for (const d of dirs) {
        const srcId = `route-${d.key}`;
        const layerId = `l-route-${d.key}`;
        const arrowLayerId = `l-route-${d.key}-arrows`;
        if (!d.coords || d.coords.length < 2) continue;
        const feature: GeoJSON.Feature = { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: d.coords } };
        const source = map.getSource(srcId) as maplibregl.GeoJSONSource | undefined;
        if (source) {
          source.setData(feature);
        } else {
          map.addSource(srcId, { type: 'geojson', data: feature, lineMetrics: true });
          map.addLayer({
            id: layerId,
            type: 'line',
            source: srcId,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': d.color, 'line-width': 4 },
          });
          // Flechas de sentido — solo SERVICIO (mode 'overview'); en
          // Velocidades/Infraestructura/Conectividad el gradiente de color
          // ya ocupa la línea. `symbol-placement:'line'` orienta el
          // glyph siguiendo el orden real de `d.coords` (la polilínea YA
          // armada para esta variante/sentido), nunca la digitalización
          // cruda del canonical edge — así IDA y VUELTA muestran cada
          // una su sentido real sin depender de heurísticas por edge.
          map.addLayer({
            id: arrowLayerId,
            type: 'symbol',
            source: srcId,
            // `symbol-spacing` es en PIXELS de pantalla, no metros — con
            // spacing chico y una línea larga (~20km), a zoom alto esto
            // generaba miles de instancias sin culling (`allow-overlap`+
            // `ignore-placement`) y colgaba el render. Spacing grande +
            // colisión activada (sin *-overlap/*-ignore-placement) para
            // que MapLibre descarte instancias redundantes.
            layout: {
              'symbol-placement': 'line',
              'symbol-spacing': 220,
              // '➤' (dingbat, U+27A4) nunca se veía — faltaba `text-font`
              // (sin fuente explícita MapLibre no resuelve el glyph) y,
              // aun agregándola, los sets de glyphs servidos por
              // proveedores de tiles rara vez cubren el bloque Dingbats.
              // '→' (U+2192, bloque Arrows) es texto normal, presente en
              // cualquier fuente general — mismo font-stack que ya
              // funciona para los números de parada.
              'text-field': '→',
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'text-size': 14,
              'text-rotation-alignment': 'map',
              'text-keep-upright': false,
            },
            paint: {
              'text-color': d.color,
              'text-halo-color': '#071827',
              'text-halo-width': 1,
              'text-opacity': 0.75,
            },
          });
        }
        if (map.getLayer(arrowLayerId)) {
          map.setLayoutProperty(arrowLayerId, 'visibility', mode === 'overview' ? 'visible' : 'none');
        }
      }
    };
    runWhenReady(map, apply);
  }, [mode, props.ida, props.vuelta, props.isCircuit]);

  // ---- Model C / SERVICIO — facilities por variante seleccionada. Fuente
  // independiente de `transit_stops`: solo puntos físicos persistidos en
  // Model C, con selección y numeración visible únicamente a zoom alto.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const stops = props.serviceStops ?? [];
    const apply = (): void => {
      const data = serviceStopsToGeoJSON(stops, props.selectedServiceStopId);
      const source = map.getSource('model-c-service-stops') as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(data);
      } else {
        map.addSource('model-c-service-stops', { type: 'geojson', data });
        map.addLayer({
          id: 'l-model-c-service-stop-selected',
          type: 'circle',
          source: 'model-c-service-stops',
          filter: ['==', ['get', 'selected'], true],
          paint: {
            'circle-radius': 12,
            'circle-color': '#ffffff',
            'circle-opacity': 0.9,
            'circle-stroke-color': '#071827',
            'circle-stroke-width': 2,
          },
        });
        map.addLayer({
          id: 'l-model-c-service-stops',
          type: 'circle',
          source: 'model-c-service-stops',
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 14, 6, 16, 8],
            'circle-color': '#f4c542',
            'circle-stroke-color': '#071827',
            'circle-stroke-width': 1.8,
          },
        });
        map.addLayer({
          id: 'l-model-c-service-stop-labels',
          type: 'symbol',
          source: 'model-c-service-stops',
          minzoom: 14,
          layout: {
            'text-field': ['get', 'label'],
            'text-size': 10,
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-offset': [0, 0],
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#071827',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.2,
          },
        });
        map.on('click', 'l-model-c-service-stops', e => {
          const facilityId = e.features?.[0]?.properties?.facilityId as string | undefined;
          const stop = facilityId ? serviceStopsRef.current.find(s => s.facilityId === facilityId) : undefined;
          if (stop) onSelectServiceStopRef.current?.(stop);
        });
        map.on('mouseenter', 'l-model-c-service-stops', () => (map.getCanvas().style.cursor = 'pointer'));
        map.on('mouseleave', 'l-model-c-service-stops', () => (map.getCanvas().style.cursor = ''));
      }
      const visibility = stops.length > 0 ? 'visible' : 'none';
      for (const layerId of ['l-model-c-service-stop-selected', 'l-model-c-service-stops', 'l-model-c-service-stop-labels']) {
        if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    };
    runWhenReady(map, apply);
  }, [props.serviceStops, props.selectedServiceStopId, props.onSelectServiceStop]);

  useEffect(() => {
    const map = mapRef.current;
    const stop = (props.serviceStops ?? []).find(s => s.facilityId === props.selectedServiceStopId);
    if (!map || !stop) return;
    const [lon, lat] = stop.displayGeometry.coordinates;
    map.easeTo({ center: [lon, lat], zoom: Math.max(map.getZoom(), 15), duration: 450 });
  }, [props.selectedServiceStopId, props.serviceStops]);

  // ---- Visibilidad cruzada entre los dos esquemas de línea — mapa
  // persistente entre pestañas: `l-route-line` (coverage) y
  // `l-route-IDA`/`l-route-VUELTA` (overview/speed) pueden coexistir en el
  // estilo (una vez creadas, nunca se destruyen) pero solo una familia
  // debe verse a la vez, o quedan ambas dibujadas superpuestas (bug real,
  // encontrado en QA: al entrar a Cobertura después de Velocidades se veían
  // IDA+VUELTA de fondo además de la línea de cobertura). ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = (): void => {
      const showSingle = mode === 'coverage' ? 'visible' : 'none';
      const showDual = mode !== 'coverage' ? 'visible' : 'none';
      if (map.getLayer('l-route-line')) map.setLayoutProperty('l-route-line', 'visibility', showSingle);
      if (map.getLayer('l-route-IDA')) map.setLayoutProperty('l-route-IDA', 'visibility', showDual);
      if (map.getLayer('l-route-VUELTA')) map.setLayoutProperty('l-route-VUELTA', 'visibility', showDual);
    };
    runWhenReady(map, apply);
  }, [mode]);

  // ---- Gradiente de velocidad/pavimento (modos `speed`/`infrastructure`)
  // — aplica/limpia `line-gradient` sobre las capas route-IDA/route-VUELTA
  // ya creadas por el efecto de arriba, sin tocar geometría. ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = (): void => {
      for (const dir of ['IDA', 'VUELTA'] as const) {
        const layerId = `l-route-${dir}`;
        if (!map.getLayer(layerId)) continue;
        const stops =
          mode === 'speed'
            ? props.speedStops?.[dir]
            : mode === 'infrastructure'
              ? props.pavementStops?.[dir]
              : mode === 'connectivity'
                ? props.syntaxStops?.[dir]
                : null;
        if (stops && stops.length > 0) {
          const expr: unknown[] = ['interpolate', ['linear'], ['line-progress']];
          for (const [p, color] of stops) expr.push(p, color);
          map.setPaintProperty(layerId, 'line-gradient', expr);
        } else {
          map.setPaintProperty(layerId, 'line-gradient', undefined);
        }
      }
    };
    runWhenReady(map, apply);
  }, [mode, props.speedStops, props.pavementStops, props.syntaxStops]);

  // ---- Capas POI + isócrona — mismo contenido/lógica que siempre, ahora
  // crear-o-actualizar. Se crean tanto en `coverage` como en
  // `infrastructure` (cada una muestra un subconjunto distinto de
  // `LAYERS` vía `layerScope`); en overview/speed no se crean (si ya
  // existían de una visita previa en el mismo mapa persistente, el efecto
  // de visibilidad de abajo las oculta igual). ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || (mode !== 'coverage' && mode !== 'infrastructure') || !props.routeMetrics) return;
    const apply = (): void => {
      for (const layer of LAYERS) {
        const isPointLayer = layer.kind === 'point';
        const features = isPointLayer
          ? pointFeaturesFor(layer.key as PointLayerKey, props.routeMetrics, props.coverageEntities).features
          : [];
        const areaFeatures = !isPointLayer ? props.coverageEntities?.[layer.key as AreaLayerKey].features ?? [] : [];
        const data = isPointLayer ? pointsToGeoJSON(features, layer.key) : areasToGeoJSON(areaFeatures, layer.key);
        const srcId = `src-${layer.key}`;
        const existing = map.getSource(srcId) as maplibregl.GeoJSONSource | undefined;
        if (existing) {
          existing.setData(data);
          continue;
        }
        map.addSource(srcId, { type: 'geojson', data });
        const visible = isLayerVisible(layer) ? 'visible' : 'none';
        if (isPointLayer) {
          map.addLayer({
            id: `l-${layer.key}`,
            type: 'circle',
            source: srcId,
            layout: { visibility: visible },
            paint: { 'circle-radius': 5, 'circle-color': layer.color, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1.5 },
          });
        } else {
          map.addLayer({
            id: `l-${layer.key}`,
            type: 'fill',
            source: srcId,
            layout: { visibility: visible },
            paint: { 'fill-color': layer.color, 'fill-opacity': ['match', ['get', 'tier'], 'GEOMETRICALLY_NEAR', 0.1, 0.35] },
          });
          map.addLayer({
            id: `l-${layer.key}-outline`,
            type: 'line',
            source: srcId,
            layout: { visibility: visible },
            paint: { 'line-color': layer.color, 'line-width': 1.5 },
          });
        }
        map.on('click', `l-${layer.key}`, e => {
          const p = e.features?.[0]?.properties;
          if (!p) return;
          const secondaryType = layer.key === 'secondary' ? (p.entityType as string) : null;
          props.onSelectFeature({
            kind: isPointLayer ? 'point' : 'area',
            category: secondaryType ? SECONDARY_TYPE_LABELS[secondaryType] ?? layer.label : layer.label,
            name: (p.name as string) ?? null,
            osmSubtype: (p.osmSubtype as string) ?? null,
            hasNetworkAssociation: isPointLayer ? Boolean(p.hasNetworkAssociation) : undefined,
            tier: !isPointLayer ? (p.tier as AreaAccessibilityTier) : undefined,
          });
        });
        map.on('mouseenter', `l-${layer.key}`, () => (map.getCanvas().style.cursor = 'pointer'));
        map.on('mouseleave', `l-${layer.key}`, () => (map.getCanvas().style.cursor = ''));
      }
    };
    runWhenReady(map, apply);
  }, [mode, props.routeMetrics, props.coverageEntities, showPois, layerScope, props.activeLayers, props.onSelectFeature]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = (): void => {
      for (const layer of LAYERS) {
        const visible = isLayerVisible(layer) ? 'visible' : 'none';
        if (map.getLayer(`l-${layer.key}`)) map.setLayoutProperty(`l-${layer.key}`, 'visibility', visible);
        if (map.getLayer(`l-${layer.key}-outline`)) map.setLayoutProperty(`l-${layer.key}-outline`, 'visibility', visible);
      }
    };
    runWhenReady(map, apply);
  }, [showPois, layerScope, props.activeLayers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = (): void => {
      const visible = showPois && props.showCoverage ? 'visible' : 'none';
      if (map.getLayer('l-coverage-fill')) map.setLayoutProperty('l-coverage-fill', 'visibility', visible);
      if (map.getLayer('l-coverage-outline')) map.setLayoutProperty('l-coverage-outline', 'visibility', visible);
    };
    runWhenReady(map, apply);
  }, [showPois, props.showCoverage]);

  // El polígono de cobertura cambia sin remontar el mapa (ej. al ajustar
  // tiempo/velocidad de caminata con la misma dirección, o al mantener el
  // mismo mapa persistente entre pestañas) — crear-o-actualizar.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== 'coverage') return;
    const apply = (): void => {
      const source = map.getSource('coverage-polygon') as maplibregl.GeoJSONSource | undefined;
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        properties: {},
        geometry: props.coveragePolygon ?? { type: 'Polygon', coordinates: [] },
      };
      if (source) {
        source.setData(feature);
      } else if (props.coveragePolygon) {
        map.addSource('coverage-polygon', { type: 'geojson', data: feature });
        const visible = showPois && props.showCoverage ? 'visible' : 'none';
        map.addLayer({
          id: 'l-coverage-fill',
          type: 'fill',
          source: 'coverage-polygon',
          layout: { visibility: visible },
          paint: { 'fill-color': '#0b6bcb', 'fill-opacity': 0.08 },
        });
        map.addLayer({
          id: 'l-coverage-outline',
          type: 'line',
          source: 'coverage-polygon',
          layout: { visibility: visible },
          paint: { 'line-color': '#0b6bcb', 'line-width': 1, 'line-opacity': 0.4 },
        });
      }
    };
    runWhenReady(map, apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showCoverage se maneja en su propio efecto; acá solo importa cuándo cambia el polígono
  }, [mode, props.coveragePolygon]);

  return <div ref={containerRef} className={props.className ?? 'ft-territorial-map'} />;
}
